import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import templateRowStyles from './TemplateRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import TemplateRow from './TemplateRow';
import ListContent from '../composites/ListContent';
import AddTemplateDialog from './AddTemplateDialog';
import EditRoutineTemplate from './EditRoutineTemplate';
import {
  useUserAction,
  useSessionData,
  useUnsavedChanges,
} from '../../providers';
import { GymType } from '../../constants';
import { getEmptyTemplate } from '../../utils/templates';

import type { TrainingRoutineTemplate, WithOptionalId } from '../../types';

const templateListTitles = [
  'Workout Name',
  'Exercises',
  'Created By',
  'Last Edit',
  'Options',
];

const RoutineTemplates: React.FC = () => {
  const { loginTrainer, gym, trainers, templates } = useSessionData();
  const { createRoutineTemplate, updateTemplate, deleteTemplate } =
    useUserAction();
  const { confirmExitIfNeeded } = useUnsavedChanges();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false);
  const [editedTemplate, setEditedTemplate] =
    useState<WithOptionalId<TrainingRoutineTemplate> | null>(null);

  const initialTemplateRef =
    useRef<WithOptionalId<TrainingRoutineTemplate> | null>(null);
  const saveRef = useRef<() => Promise<void> | void>(() => { });

  useEffect(() => {
    if (editedTemplate && !initialTemplateRef.current) {
      initialTemplateRef.current = _.cloneDeep(editedTemplate);
    } else if (!editedTemplate) {
      initialTemplateRef.current = null;
    }
  }, [editedTemplate]);

  const isGymOfTypeSchool = gym?.type === GymType.School;

  const filteredTemplates = useMemo(() => {
    if (!Object.values(templates).length) return [];
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return Object.values(templates)
      .filter(({ name }) =>
        name?.toLowerCase().includes(normalizedQuery),
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [searchQuery, templates]);

  // Moved above early return to satisfy rules-of-hooks
  const handleEditTemplate = useCallback(
    async (
      newTemplateData: WithOptionalId<TrainingRoutineTemplate>,
    ): Promise<boolean> => {
      if (!loginTrainer) return false;
      const success = newTemplateData.id
        ? await updateTemplate(newTemplateData as TrainingRoutineTemplate)
        : await createRoutineTemplate(newTemplateData, loginTrainer.id);
      if (!success) {
        return false;
      }
      setEditedTemplate(null);
      setIsAddTemplateModalOpen(false);
      return true;
    },
    [updateTemplate, createRoutineTemplate, loginTrainer],
  );

  // Moved above early return to satisfy rules-of-hooks
  const handleCloseEditTemplate = useCallback(() => {
    setEditedTemplate(null);
  }, []);

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<FitnessCenterOutlinedIcon />}
        text="No workouts for your role."
      />
    );
  }

  saveRef.current = async () => {
    if (editedTemplate) {
      await handleEditTemplate(editedTemplate);
    }
  };

  const renderEmptyState = () => (
    <EmptyState
      icon={<FitnessCenterOutlinedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add A Workout',
        onClick: () => {
          if (isGymOfTypeSchool) {
            const defaultTemplate = getEmptyTemplate(
              'New Workout',
              0,
              '',
              true,
              loginTrainer.id,
            );
            setEditedTemplate(defaultTemplate);
          } else {
            setIsAddTemplateModalOpen(true);
          }
        },
      }}
      text="No Workouts Have Been Added"
    />
  );

  const renderAddTemplateDialog = () => (
    <AddTemplateDialog
      title="Create A Template"
      trainerId={loginTrainer.id}
      addTemplate={setEditedTemplate}
      onClose={() => setIsAddTemplateModalOpen(false)}
      isOpen={isAddTemplateModalOpen}
    />
  );

  const discardTemplate = async (template: TrainingRoutineTemplate) => {
    await deleteTemplate(template);
  };

  const duplicateTemplate = async (template: TrainingRoutineTemplate) => {
    const newTemplate = {
      ...template,
      name: `${template.name} - Copy`,
      routine: {
        ...template.routine,
        name: `${template.name} - Copy`,
      },
      id: undefined,
    };
    await createRoutineTemplate(newTemplate, loginTrainer.id);
  };

  const renderContent = () => {
    if (editedTemplate) return null;
    return (
      <ListContent
        items={filteredTemplates}
        listHeaderClassName={templateRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={templateListTitles}
        ItemComponent={({ data }) => (
          <TemplateRow
            template={data}
            deleteTemplate={discardTemplate}
            allowDelete={loginTrainer.id === data.trainerIds[0]}
            duplicateTemplate={duplicateTemplate}
            openEditTemplateModal={() => setEditedTemplate(data)}
            selected={selectedTemplate === data.id}
            onSelect={() => setSelectedTemplate(data.id)}
            key={data.id}
            trainer={trainers[data.trainerIds[0]]?.trainer}
          />
        )}
      />
    );
  };

  const getDrillInContent = () => {
    if (!editedTemplate) return null;
    return (
      <EditRoutineTemplate
        onSubmit={handleEditTemplate}
        template={editedTemplate}
        setTemplate={
          setEditedTemplate as React.Dispatch<
            React.SetStateAction<WithOptionalId<TrainingRoutineTemplate>>
          >
        }
        onClose={handleCloseEditTemplate}
        isNew={!editedTemplate.id}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredTemplates.length) return '';
    return 'Search for Workouts';
  };

  return (
    <ContentDrill
      title={editedTemplate ? 'Edit Workout' : 'Workout Library'}
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Workout',
          onClick: () => {
            if (isGymOfTypeSchool) {
              const defaultTemplate = getEmptyTemplate(
                'New Workout',
                0,
                '',
                true,
                loginTrainer.id,
              );
              setEditedTemplate(defaultTemplate);
            } else {
              setIsAddTemplateModalOpen(true);
            }
          },
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
      isDrilledIn={Boolean(editedTemplate)}
      drillInContent={getDrillInContent()}
      onBack={
        editedTemplate
          ? () => confirmExitIfNeeded(handleCloseEditTemplate)
          : undefined
      }
    >
      {renderContent()}
      {!isGymOfTypeSchool && renderAddTemplateDialog()}
    </ContentDrill>
  );
};

export default RoutineTemplates;